import { Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  DirectDebit,
  EditableDirectDebit,
  EditablePaymentMethod,
  isReusablePaymentMethod,
  PaymentMethodType,
  ReusablePaymentMethod
} from '@platform-frontend/marketplace';
import { DirectDebitService } from '@platform-frontend/payment-util';
import { InvalidStateError } from '@platform-frontend/util';
import { Observable, of } from 'rxjs';
import { CheckoutPaymentInformationState } from './checkout-payment-information.state';

@Injectable({
  providedIn: 'root'
})
export class PaymentMethodDataService {
  constructor(private directDebitService: DirectDebitService, private store: Store) {}

  getEditablePaymentMethod(id: number | null, createType: PaymentMethodType | null = null): Observable<Partial<EditablePaymentMethod>> {
    const paymentMethod =
      this.store
        .selectSnapshot(CheckoutPaymentInformationState.paymentMethods)
        .filter((method): method is ReusablePaymentMethod => isReusablePaymentMethod(method))
        .find(method => method.id === id) ?? null;

    if (paymentMethod === null) {
      return this.getEmptyPaymentMethod(createType);
    } else if (paymentMethod.type === 'DIRECT_DEBIT') {
      return this.convertDirectDebit(paymentMethod);
    } else {
      throw new InvalidStateError('Error while loading payment method');
    }
  }

  private getEmptyPaymentMethod(createType: PaymentMethodType | null): Observable<Partial<EditablePaymentMethod>> {
    if (createType === 'CREDIT_CARD' || createType === 'DIRECT_DEBIT') {
      return of({ type: createType, id: 0 });
    } else {
      throw new InvalidStateError('Cannot create payment method of type: ' + createType);
    }
  }

  private convertDirectDebit(paymentMethod: DirectDebit): Observable<EditableDirectDebit> {
    return of({
      type: 'DIRECT_DEBIT',
      id: paymentMethod.id,
      accountOwner: paymentMethod.accountOwner,
      iban: this.directDebitService.formatIban(paymentMethod.iban),
      bank: paymentMethod.bank,
      bic: paymentMethod.bic
    });
  }
}
